import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Icon,
  Illustration,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  APPLICATION_WORKFLOW_KEYS,
  CF_CRT_MED,
  CF_NEU_BLD,
  CF_NEU_INV,
  CF_NEU_MED,
  CF_NEU_STL,
  CF_SEC_BLD,
  LIMIT_INLINE_COUNT,
  testIds,
} from '../../../constants';
import { useEffectAfterMount, useInViewObserver } from '../../../hooks';
import { getLogoEndpointUrl } from '../../../utils';
import { WRAPPER_MODE } from '../../UiWrapper';
import { MAX_COUNT } from '../constants';
import { ListControls, ListSkeleton } from '../Shared';
import {
  type ApplicationListResponse,
  type CustomListModalTypes,
  type EndpointParams,
} from '../types';

import './ApplicationModal.css';

interface TrackedListItem extends ApplicationListResponse {
  tracker: {
    id: number;
    isChecked: boolean;
  };
}

interface ApplicationListItemProps {
  isLoading: boolean;
  listItem: TrackedListItem;
  handleClick: (id: number) => void;
}

function ApplicationListItem({
  isLoading,
  listItem,
  handleClick,
}: ApplicationListItemProps) {
  const {
    applicationName,
    applicationDisplayName,
    applicationLogo,
    applicationDescription,
    accountCreationAllowed,
    recommended,
    tracker: { id, isChecked },
  } = listItem;

  const isDisabled = Boolean(isLoading || accountCreationAllowed === false);
  const isRecommended = Boolean(recommended);

  return (
    <button
      type='button'
      aria-disabled='false'
      tabIndex={isDisabled ? -1 : 0}
      data-testid={`${testIds.LIST_VIEWER_APPLICATION_ITEM_BTN}-${id}`}
      onClick={() => {
        if (!isDisabled) {
          handleClick(id);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.detail === 1 && !isDisabled) {
          handleClick(id);
        }
      }}
      className={cx(
        'ApplicationListItem',
        isChecked && 'ApplicationListItem--isChecked'
      )}>
      {isRecommended && (
        <Box tag='div' className='ApplicationListItem-starWrapper'>
          <Icon kind='StarFilled' size='smallMedium' color={CF_NEU_INV} />
        </Box>
      )}
      <Box tag='div' className='ApplicationListItem-iconWrapper'>
        <ApplicationIcon
          size='medium'
          src={getLogoEndpointUrl(applicationLogo)}
        />
      </Box>
      <Box tag='div' className='ApplicationListItem-contentWrapper'>
        <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1}>
          {applicationDisplayName && applicationDisplayName.trim().length > 0
            ? applicationDisplayName
            : applicationName}
        </Typography>
        <Box tag='div' className='ApplicationListItem-content'>
          <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
            {applicationDescription}
          </Typography>
        </Box>
      </Box>
      <Box
        tag='div'
        className={cx(
          'ApplicationListItem-selectWrapper',
          isRecommended && 'ApplicationListItem-selectWrapper--isRecommended'
        )}>
        <Tooltip
          trigger={
            <ButtonIcon
              size='large'
              kind='outlinedSecondary'
              isSelected={isChecked}
              icon={
                <Icon kind={isChecked ? 'Subtract' : 'Add'} size='xSmall' />
              }
              isDisabled={isDisabled}
              ariaLabel={isChecked ? 'Remove' : 'Add'}
              onClick={() => {}}
              tabIndex={-1}
            />
          }
          text={isChecked ? 'Remove' : 'Add'}
          placement='left'
        />
      </Box>
    </button>
  );
}

const DEFAULT_LIST_STATE = {
  currentOffset: 0,
  totalCount: null as number | null,
  isObserverInView: true,
  isListError: false,
  isListLoading: false,
};

interface ApplicationModalProps {
  isFullView: boolean;
  workflowType: string;
  isGetMethod: boolean;
  retentions?: ApplicationListResponse[];
  fetchList: (pagination?: EndpointParams) => Promise<unknown>;
  setChecked: React.Dispatch<React.SetStateAction<CustomListModalTypes[]>>;
  currentMode: string;
  setInlineItemCount: React.Dispatch<React.SetStateAction<number | null>>;
}

/**
 * ApplicationModal
 */
function ApplicationModal({
  isFullView,
  workflowType,
  isGetMethod,
  retentions,
  fetchList,
  setChecked,
  currentMode,
  setInlineItemCount,
}: ApplicationModalProps) {
  const [listControls, setListControls] = useState({
    searchText: '',
  });
  const [listState, setListState] = useState(DEFAULT_LIST_STATE);
  const [listRows, setListRows] = useState<TrackedListItem[]>(
    [] as TrackedListItem[]
  );
  const [recommendedMode, setRecommendedMode] = useState(1);
  const [activeRows, setActiveRows] = useState<TrackedListItem[]>([]);

  const retentionRef = useRef<TrackedListItem[]>(
    retentions?.map((item) => ({
      ...item,
      tracker: {
        id: item.applicationId,
        isChecked: true,
      },
    })) ?? []
  );

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        !listState.isListLoading &&
          (listState.totalCount === null ||
            (listState.currentOffset < listState.totalCount &&
              listRows.length < listState.totalCount))
      ),
    [
      listState.currentOffset,
      listState.totalCount,
      listRows.length,
      listState.isListLoading,
    ]
  );
  const isZeroCount = useMemo(
    () =>
      Boolean(
        listRows.length === 0 &&
          listState.totalCount !== null &&
          !isValidApiCall
      ),
    [isValidApiCall, listRows.length, listState.totalCount]
  );
  const isFilterApplied = useMemo(
    () => listControls.searchText.trim().length > 0,
    [listControls.searchText]
  );

  // List management
  const listObserverElement = useInViewObserver(({ isInView }) => {
    setListState((prev) => ({ ...prev, isObserverInView: isInView }));
  });

  useEffect(() => {
    if (!listState.isObserverInView || !isValidApiCall) {
      return;
    }

    Logger.log(
      'Fetching',
      listState.currentOffset,
      'to',
      listState.currentOffset + MAX_COUNT
    );

    setListState((prev) => ({
      ...prev,
      isListLoading: true,
      isListError: false,
    }));

    const payload = isFullView
      ? {
          max: MAX_COUNT,
          offset: listState.currentOffset,
          ...(isGetMethod
            ? {
                queryParams: {
                  wsearch: listControls.searchText,
                  ...(APPLICATION_WORKFLOW_KEYS.includes(workflowType) &&
                  recommendedMode === 2
                    ? {
                        recommended: true,
                      }
                    : {}),
                },
              }
            : {
                payload: {
                  wsearch: listControls.searchText,
                  ...(APPLICATION_WORKFLOW_KEYS.includes(workflowType) &&
                  recommendedMode === 2
                    ? {
                        filters: [
                          { field: 'recommended', op: 'eq', value: 'True' },
                        ],
                      }
                    : {}),
                },
              }),
        }
      : undefined;

    fetchList(payload)
      .then((response) => {
        const { totalRecords, data } = response as {
          totalRecords: number;
          data: ApplicationListResponse[] | null | undefined;
        };

        if (!data) {
          setListState((prev) => ({ ...prev, totalCount: 0 }));

          return;
        }

        if (
          totalRecords > LIMIT_INLINE_COUNT &&
          currentMode === WRAPPER_MODE.INLINE
        ) {
          setInlineItemCount(data.length);
        }

        let trackedResponse = data.map((item) => ({
          ...item,
          tracker: {
            id: item.applicationId,
            isChecked:
              item.isChecked ||
              Boolean(
                retentionRef.current.find(
                  (o) => o.tracker.id === item.applicationId
                )
              ),
          },
        })) as TrackedListItem[];

        if (!isFullView) {
          const retentionIds = retentionRef.current.map((r) => r.applicationId);

          trackedResponse = trackedResponse.filter(
            (item) => !retentionIds.includes(item.applicationId)
          );
        }

        const finalList = !isFullView
          ? [...retentionRef.current, ...trackedResponse].slice(0, 3)
          : trackedResponse;

        setListRows((prev) => [...prev, ...finalList]);
        setListState((prev) => ({
          ...prev,
          totalCount: totalRecords,
          currentOffset: prev.currentOffset + MAX_COUNT,
        }));
      })
      .catch((error) => {
        Logger.log('Received error while fetching: ', error);

        setListState((prev) => ({
          ...prev,
          isListError: true,
        }));
      })
      .finally(() => {
        setListState((prev) => ({
          ...prev,
          isListLoading: false,
          isObserverInView: false,
        }));
      });
  }, [
    isValidApiCall,
    fetchList,
    activeRows,
    isFullView,
    workflowType,
    isGetMethod,
    listState.isObserverInView,
    listState.currentOffset,
    listControls.searchText,
    recommendedMode,
    currentMode,
    setInlineItemCount,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffectAfterMount(() => {
    const debounceTimer = setTimeout(() => {
      setListState(DEFAULT_LIST_STATE);
      setListRows([]);
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [listControls.searchText, recommendedMode]);

  // Setting selections
  useEffect(() => {
    setActiveRows([
      ...retentionRef.current,
      ...listRows.filter((e) => e.tracker.isChecked),
    ]);
  }, [listRows]);

  useEffect(() => {
    setChecked(activeRows.map(({ tracker, ...rest }) => ({ ...rest })));
  }, [activeRows, setChecked]);

  // Utils
  const handleClick = useCallback((id: number) => {
    if (retentionRef.current.length > 0) {
      retentionRef.current = [];
    }

    setListRows((prev) =>
      prev.map((item) => {
        if (item.tracker.id === id) {
          return {
            ...item,
            tracker: {
              ...item.tracker,
              isChecked: !item.tracker.isChecked,
            },
          };
        }

        return {
          ...item,
          tracker: {
            ...item.tracker,
            isChecked: false,
          },
        };
      })
    );
  }, []);

  const handleSearch = useCallback((text: string) => {
    setListControls((prev) => ({ ...prev, searchText: text }));
  }, []);

  return (
    <Box
      tag='div'
      className='ApplicationModal'
      dataTestId={testIds.LIST_VIEWER_APPLICATION_MODAL}>
      {isFullView && (
        <>
          <Box tag='div'>
            <Typography kind='h2' color={CF_SEC_BLD}>
              <FormattedMessage
                id='hZtYH3'
                defaultMessage='Available Applications'
                description='Heading for list of applications in ApplicationModal component'
              />
            </Typography>
            <Typography kind='body2' color={CF_NEU_MED}>
              <FormattedMessage
                id='WjBmbi'
                defaultMessage='Your available options have been curated based on your previous inputs. To see a wider or narrower list of options, update it with a prompt.'
                description='Your available options have been curated based on your previous inputs. To see a wider or narrower list of options, update it with a prompt.'
              />
            </Typography>
          </Box>
          <Box tag='div' className='ApplicationModal-controls'>
            <ListControls
              setSearchText={handleSearch}
              isDisabled={listState.isListLoading}
            />
            {APPLICATION_WORKFLOW_KEYS.includes(workflowType) && (
              <ToggleButtonGroup
                selection={recommendedMode}
                setSelection={setRecommendedMode}
                isDisabled={listState.isListLoading}>
                <ToggleButton
                  label='All'
                  size='medium'
                  isDisabled={listState.isListLoading}
                />
                <ToggleButton
                  label='Recommended'
                  size='medium'
                  isDisabled={listState.isListLoading}
                />
              </ToggleButtonGroup>
            )}
          </Box>
        </>
      )}
      <Box tag='div' className='ApplicationModal-bodyWrapper'>
        {listRows.map((item) => (
          <ApplicationListItem
            key={item.tracker.id}
            isLoading={listState.isListLoading}
            listItem={item}
            handleClick={handleClick}
          />
        ))}
        {listState.isListError && (
          <Box className='ListViewer-errorModal'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              <FormattedMessage
                id='apz7lc'
                defaultMessage='System encountered an error. Please contact the Support Administrator'
                description='System encountered an error. Please contact the Support Administrator'
              />
            </Typography>
          </Box>
        )}
        {/* Observer Element */}
        {!listState.isListError && !listState.isListLoading && isFullView
          ? listObserverElement
          : null}
        {/* Loading Skeleton */}
        {!listState.isListError && listState.isListLoading && (
          <Box className='ListViewer-loadingModal'>
            <ListSkeleton count={3} />
          </Box>
        )}
        {/* With empty controls */}
        {!listState.isListError && isZeroCount && (
          <Box tag='div' className='AccessModal-illustrationWrapper'>
            <Illustration kind='Empty' />
            <Typography kind='h1'>
              {isFilterApplied ? (
                <FormattedMessage
                  id='C0QjhC'
                  defaultMessage='No Results Found!'
                  description='No Results Found!'
                />
              ) : (
                <FormattedMessage
                  id='CatMdT'
                  defaultMessage='There are no eligible Applications to select!'
                  description='Message displayed when there are no applications to display in ApplicationModal component'
                />
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

ApplicationModal.defaultProps = {
  retentions: null,
};

export default memo(ApplicationModal);
